"use client";

import { useEffect, useRef, useState } from "react";
import { Button } from "@focal/ui-kit";

interface ArcadeEmbedProps {
  url: string;
  title?: string;
  onClose: () => void;
  buttonText?: string;
  onFinalStep?: () => void;
}

export function ArcadeEmbed({
  url,
  title = "Arcade tutorial",
  onClose,
  buttonText = "Close",
  onFinalStep,
}: ArcadeEmbedProps) {
  const [isLoaded, setIsLoaded] = useState(false);
  // Arcade emits `isFinalStep: true` more than once per session (revisits, re-paints),
  // so fire onFinalStep at most once per mount.
  const hasFiredFinalStepRef = useRef(false);

  useEffect(() => {
    if (!onFinalStep) return;

    const handleMessage = (event: MessageEvent) => {
      if (
        event.data?.eventSource === "Arcade" &&
        event.data?.isFinalStep === true &&
        !hasFiredFinalStepRef.current
      ) {
        hasFiredFinalStepRef.current = true;
        onFinalStep();
      }
    };

    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, [onFinalStep]);

  return (
    <div className="flex flex-col h-full bg-background">
      <div className="flex-1 relative">
        {!isLoaded && (
          <div className="absolute inset-0 flex items-center justify-center bg-background z-10">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
          </div>
        )}
        <iframe
          src={url}
          title={title}
          loading="eager"
          allowFullScreen
          allow="clipboard-write"
          sandbox="allow-scripts allow-same-origin"
          className="w-full h-full border-0"
          style={{ colorScheme: "light" }}
          onLoad={() => setIsLoaded(true)}
        />
      </div>
      <div className="p-2.5 flex justify-center bg-background border-t border-border">
        <Button variant="ghost" size="default" onClick={onClose}>
          {buttonText}
        </Button>
      </div>
    </div>
  );
}
